import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../store/store";
import { Checkbox } from "@mui/material";
import PurchaseLayout from "./components/PurchaseLayout";
import { getAuthListing, getListing } from "../../../../../apis/listings/buyer";
import {
  FirstbankLogo,
  FlutterwaveLogo,
  PaystackLogo,
} from "../../../../common/components/LayoutIcons";
import LeftSubSection from "./components/LeftSubSection";
import ConfirmationModal from "../../../../common/components/ConfirmationModal";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  confirmPayment,
  getBankAccount,
  getPaymentInvoiceData,
} from "../../../../../apis/buyer/payment";
import { fetchAuthListingSuccess } from "../../../../../store/slices/buyer/listingsSlice";
import { errorToastify } from "../../../../common/utils/Toasty";
import { formatNumberString } from "../../../../common/utils/helpers";

const PaymentMethodBox = ({
  icon,
  text,
  active,
  disabled,
  setPaymentMethod,
}: {
  icon: JSX.Element;
  text: string;
  active?: boolean;
  disabled?: boolean;
  setPaymentMethod: (value: string) => void;
}) => (
  <div
    className={`${active ? "card-shadow" : ""} ${
      disabled
        ? "opacity-30 cursor-not-allowed"
        : "cursor-pointer hover:bg-[#F5F5F5] transition-all duration-200 ease-in-out"
    } flex flex-col items-center gap-y-[8px] p-[14px] w-[185px] border border-[#CECECE]`}
    style={{ borderRadius: 4 }}
    onClick={() => (disabled ? {} : setPaymentMethod(text))}
  >
    {icon}
    <div className="text-[18px] text-[#5E5E5E] font-light">{text}</div>
  </div>
);

const PurchaseCheckout = () => {
  const navigate = useNavigate();
  const { lid: listingId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const listing = useSelector((state: RootState) => state.listings);
  const user = useSelector((state: RootState) => state.user);

  const [paymentMethod, setPaymentMethod] = useState("First Bank");
  const [reRegistration, setReRegistration] = useState(false);
  const [purchaseRequestId, setPurchaseRequestId] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [consent, setConsent] = useState(false);

  const { data: listingData, isLoading: isListingLoading } = useQuery({
    queryKey: ["listing", { listingId, getRef: true }],
    queryFn: getAuthListing,
  });

  useEffect(() => {
    if (listingData?.lid && listingData?.lid !== listing.listing?.lid) {
      dispatch(fetchAuthListingSuccess(listingData));
    }
  }, [listingData]);

  const { data: bankAccount, isLoading: isBankAccountLoading } = useQuery({
    queryKey: ["bankAccount"],
    queryFn: getBankAccount,
  });

  const { data: purchaseInvoice, isLoading: isPurchaseInvoiceLoading } =
    useQuery({
      queryKey: ["purchaseInvoice", { referenceId: purchaseRequestId }],
      queryFn: getPaymentInvoiceData,
    });

  useEffect(() => {
    if ((bankAccount as any)?.data?.requires_reregistration) {
      errorToastify((bankAccount as any)?.message);
      setReRegistration(true);
    }
  }, [bankAccount]);

  useEffect(() => {
    if (
      bankAccount?.account_number &&
      listingData?.purchase_request?.reference_id
    ) {
      setPurchaseRequestId(listingData?.purchase_request?.reference_id);
    }
  }, [bankAccount, listingData]);

  const doPaymentConfirmation = useMutation({
    mutationFn: confirmPayment,
    onSuccess: (data) => {
      if (data !== "error") {
        setShowSuccessModal(true);
      }
    },
    onError: (error) => {
      // TODO: Handle error, e.g., show an error message to the user.
    },
  });

  return (
    <PurchaseLayout heading="Purchase checkout">
      <>
        <div className="flex-1 flex flex-col gap-y-[15px] sm:min-w-[700px] rounded-[8px]">
          <LeftSubSection title="Buyer information">
            <div className="responsive_flex gap-x-[25px] justify-between">
              <div className="flex flex-col gap-y-[8px]">
                <div className="text-[#5E5E5E] text-[16px]">Full name</div>
                <div className="text-[#000000] text-[18px]">
                  {`${user.user.first_name} ${user.user.last_name}`}
                </div>
              </div>
              <div className="flex flex-col gap-y-[8px]">
                <div className="text-[#5E5E5E] text-[16px]">Email address</div>
                <div className="text-[#000000] text-[18px]">
                  {user.user.email}
                </div>
              </div>
              <div className="flex flex-col gap-y-[8px]">
                <div className="text-[#5E5E5E] text-[16px]">Phone number</div>
                <div className="text-[#000000] text-[18px]">+234-812345678</div>
              </div>
            </div>
          </LeftSubSection>
          <LeftSubSection title="Choose payment method">
            <div className="responsive_flex gap-x-[15px] flex-wrap items-center">
              <PaymentMethodBox
                text="First Bank"
                icon={<FirstbankLogo style={{ maxHeight: 20 }} />}
                active={paymentMethod === "First Bank"}
                setPaymentMethod={setPaymentMethod}
              />
              {/* <PaymentMethodBox
                text="Flutterwave"
                icon={<FlutterwaveLogo style={{ maxHeight: 20, opacity: 0 }} />}
                active={paymentMethod === "Flutterwave"}
                setPaymentMethod={setPaymentMethod}
                disabled
              />
              <PaymentMethodBox
                text="Paystack"
                icon={<PaystackLogo style={{ maxHeight: 20, opacity: 0 }} />}
                active={paymentMethod === "Paystack"}
                setPaymentMethod={setPaymentMethod}
                disabled
              /> */}
            </div>
          </LeftSubSection>
          {!!reRegistration && (
            <div className="p-[10px] bg-[#f2dfdf] rounded-[5px]">
              {(bankAccount as any)?.message}
            </div>
          )}
          {!!bankAccount?.account_number ? (
            <LeftSubSection
              title="Payment information"
              styleClass="min-h-[200px]"
            >
              {paymentMethod === "First Bank" && (
                <>
                  <div className="text-[#5E5E5E] text-[14px] font-light">
                    You can now proceed to make payment into the account details
                    below using any of your preferred channels - internet
                    banking, intra/inter-bank transfer, direct deposit, etc.
                  </div>

                  <div className="responsive_flex gap-x-[15px]">
                    <div className="flex-1">
                      <div className="custom__label">Account name</div>
                      <input
                        type="text"
                        disabled
                        value={bankAccount.account_name}
                        className="standard_form_input"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="custom__label">Amount</div>
                      <input
                        type="text"
                        disabled
                        value={formatNumberString(
                          purchaseInvoice?.total_payable
                        )}
                        className="standard_form_input"
                        style={{ letterSpacing: "1.5px" }}
                      />
                    </div>
                  </div>
                  <div className="responsive_flex gap-x-[15px]">
                    <div className="flex-1">
                      <div className="custom__label">Bank name</div>
                      <input
                        type="text"
                        disabled
                        value={bankAccount.bank_name}
                        className="standard_form_input"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="custom__label">Account number</div>
                      <input
                        type="text"
                        disabled
                        value={bankAccount.account_number}
                        className="standard_form_input"
                        style={{ letterSpacing: "1.5px" }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-x-[15px] items-center">
                    {/* <input type="checkbox" id="terms" /> */}
                    <div className="max-h-[20px] min-w-[20px] rounded-[50%] bg-[#CD0101] flex items-center justify-center text-[#FFFFFF] text-[18px]">
                      !
                    </div>

                    <div className="font-light text-[#5E5E5E] text-[14px]">
                      Once you have made the payment, kindly click on the button
                      below to log the payment on the Home and Abroad platform.
                    </div>
                  </div>

                  <button
                    className="filled-button w-full"
                    onClick={() =>
                      listingId
                        ? doPaymentConfirmation.mutate({
                            referenceId: listingId,
                          })
                        : {}
                    }
                  >
                    I HAVE MADE PAYMENT
                  </button>
                </>
              )}
            </LeftSubSection>
          ) : (
            <LeftSubSection
              title={
                <div className="flex flex-col gap-y-[4px] text-center">
                  <div className="">First Bank</div>
                  <div className="text-[#5E5E5E] text-[16px] font-light">
                    Start Your Secure Payment With First Bank
                  </div>
                </div>
              }
            >
              <div
                className="bg-[#066D0E14] text-[#5E5E5E] text-[14px] font-light px-[14px] py-[16px]"
                style={{ borderRadius: 8 }}
              >
                Since this is your first purchase with us using First Bank, a
                unique property purchase account will be created in your name.
                This account helps streamline your payments securely and ensures
                full traceability between you, your property, and the associated
                transaction. <br />
                <br />
                To proceed, you'll need to open a dedicated First Bank account.
                This account will be used exclusively for this property purchase
                and any future purchases on our platform. Once created, all
                transaction activities will be linked to this account for
                transparency, audit, and documentation purposes.
                <div
                  className="bg-[#FFFFFF] text-[#066D0E] px-[16px] py-[10px] mt-[20px]"
                  style={{ borderRadius: 8 }}
                >
                  <div></div>
                  <div>
                    Don't worry — opening the account is free, fast, and takes
                    few minutes. Next, you’ll be guided through a form, and once
                    it’s complete, you’ll receive your new account number
                    instantly.
                  </div>
                </div>
              </div>

              <div className="flex gap-x-[15px]">
                {/* <input type="checkbox" id="terms" /> */}
                <Checkbox
                  id="terms"
                  color="primary"
                  onChange={() => setConsent((currentState) => !currentState)}
                  // checked={isItemSelected}
                />

                <label
                  htmlFor="terms"
                  className="custom__label font-light text-[#5E5E5E]"
                  style={{ marginBottom: 0 }}
                >
                  By clicking this you acknowledge that you have read and
                  accepted the{" "}
                  <a href="#fff" className="text-[#066D0E]">
                    data consent form
                  </a>
                  .
                </label>
              </div>

              <button
                className="filled-button w-full"
                disabled={!consent}
                onClick={() => navigate(`/purchase/account-setup/${listingId}`)}
              >
                OPEN HOME & ABROAD ACCOUNT
              </button>
            </LeftSubSection>
          )}
        </div>
        <ConfirmationModal
          showModal={showSuccessModal}
          onCloseModal={() => setShowSuccessModal(false)}
          headerText="Congratulations! Payment Successful"
          btnText="Continue"
          additionalInfoDiv={
            <div className="bg-[#066D0E14] p-[15px] flex flex-col gap-y-[20px] rounded-[8px] items-center">
              <div className="grid gap-[10px] [grid-template-columns:25px_auto]">
                <div className="flex justify-center items-center rounded-[50%] bg-[#066D0E] text-[#FFFFFF] text-[14px] w-[20px] h-[20px]">
                  i
                </div>
                <div className="flex items-center text-[#066D0E] text-[16px]">
                  What happens next?
                </div>
                <div></div>
                <div className="text-[13px] text-[#363636] font-light">
                  <div>
                    Once the seller uploads the required documents, you’ll
                    receive a notification email to review and approve them.
                    After your approval, payment will be released to the seller,
                    and the property transfer process will be initiated.
                  </div>
                </div>
              </div>
            </div>
          }
          btnActtion={() => {
            navigate(`/dashboard/transactions`);
          }}
        >
          <div>
            <div className="text-[16px] text-[#5E5E5E] font-[400]">
              Next Step: Await Document Upload
            </div>
            <div className="p-[15px] font-light flex flex-col gap-y-[20px] mt-[25px] rounded-[8px] items-center">
              Thank you! Your payment for the property has been successfully
              processed. Kindly note that First Bank has placed a lien on the
              amount paid as the seller uploads all property documents on the
              platform.
            </div>
          </div>
        </ConfirmationModal>
      </>
    </PurchaseLayout>
  );
};

export default PurchaseCheckout;
